import { format, isValid, parseISO } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { getLocalTimeZone } from './date';

// Here functions that are only needed temporarily.
// At the moment the backend often uses EST instead of UTC as it should,
// so after migrating to UTC we will remove the corresponding functions.
// They are intentionally implemented in a simple way to replace the places
// where we are still using moment.

/**
 * Convert a datetime string in America/New_York (EST/EDT) to local timezone.
 * @param estDateTime - e.g. "2025-09-25T08:05:32.97"
 * @returns local datetime in system time zone, e.g. "2025-09-25T15:05:32.97"
 */
export function convertFromESTToLocal(estDateTime: string | null): string {
  if (!estDateTime) {
    return '';
  }
  const utcDate = fromZonedTime(estDateTime, 'America/New_York');

  const localDate = toZonedTime(utcDate, getLocalTimeZone());

  return format(localDate, "yyyy-MM-dd'T'HH:mm:ss.SS");
}

/**
 * Converts a given EST date string in specific format to an ISO string in UTC.
 *
 * @param {string} date - The input date string in EST (e.g., '2024-08-20T09:56:59.287').
 * @returns {string} - The normalized date as an ISO string in UTC (e.g., '2024-08-20T09:56:59.287Z').
 */
export const convertESTtoUTC = (date: string): string => {
  if (!date) {
    return '';
  }

  const parsedDate = parseISO(date);

  if (!isValid(parsedDate)) {
    return '';
  }
  // Convert EST to UTC
  const timezoneEST = 'America/New_York';
  const utcDate = fromZonedTime(parsedDate, timezoneEST);

  return utcDate.toISOString();
};

/**
 * Convert a UTC ISO string to America/New_York (EST/EDT) datetime string.
 * @param utcDateTime - e.g. "2025-09-25T15:05:32.970Z"
 * @returns datetime in New York timezone, e.g. "2025-09-25T11:05:32"
 */
export function convertFromUTCToEST(utcDateTime: string | null): string {
  if (!utcDateTime) {
    return '';
  }

  const parsed = parseISO(utcDateTime);

  if (!isValid(parsed)) {
    return '';
  }

  const newYorkDate = toZonedTime(parsed, 'America/New_York');

  return format(newYorkDate, "yyyy-MM-dd'T'HH:mm:ss");
}
